/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useRef, useState } from "react";
import { FOCUSED_CLASS, RAIL_SELECTOR } from "../constants/navigation";

interface UseRailNavigationOptions {
  readonly itemCount: number;
  readonly onAction?: (index: number) => void;
  readonly onInfo?: (index: number) => void;
  readonly onWatchlist?: (index: number) => void;
  /** When true, Enter navigates to info (title page) instead of action (play). Used for browse-only rails. */
  readonly preferInfoOnEnter?: boolean;
  /** When true, disables automatic scrolling to focused cards. Useful for carousels with custom scroll behavior. */
  readonly disableAutoScroll?: boolean;
  /** Initial focused index (default: 0). Useful for carousels that want to start on a middle item. */
  readonly initialIndex?: number;
}

interface UseRailNavigationReturn {
  readonly focusedIndex: number;
  readonly isRailFocused: boolean;
  readonly railRef: React.RefObject<HTMLElement | null>;
  readonly handleRailFocus: () => void;
  readonly handleRailBlur: () => void;
  readonly handleRailKeyDown: (e: React.KeyboardEvent) => void;
  readonly getCardProps: (index: number) => {
    readonly className: string;
    readonly isFocused: boolean;
    readonly ref: (el: HTMLElement | null) => void;
    readonly onWatchlistToggle?: () => void;
  };
  readonly registerWatchlistHandler: (index: number, handler: () => void) => void;
  /** Programmatically navigate to a specific card index (clamped to valid range) */
  readonly navigateToIndex: (index: number) => void;
} /**
 * Hook for managing roving tabindex navigation in content rails
 *
 * Provides keyboard-based navigation for horizontal content rails with card focus management.
 * Implements roving tabindex pattern for accessibility and smooth navigation between cards.
 *
 * Keyboard navigation:
 * - Tab: Focus rail (first card highlighted)
 * - ←/→: Navigate between cards within the rail
 * - ↑/↓: Move to prev/next rail (allows parent handler when at boundaries)
 * - Enter/Space: Execute primary action (play/resume or info for browse-only)
 * - i: Show info modal for focused card
 * - w: Toggle watchlist for focused card
 * - Home/End: Jump to first/last card
 * - Escape: Blur rail focus
 *
 * @param options - Configuration for rail navigation behavior
 * @param options.itemCount - Total number of items in the rail
 * @param options.onAction - Callback when Enter/Space is pressed on a card
 * @param options.onInfo - Callback when 'i' is pressed on a card
 * @param options.onWatchlist - Callback when 'w' is pressed on a card
 * @param options.preferInfoOnEnter - If true, Enter navigates to info instead of action (browse-only mode)
 *
 * @returns Rail navigation state and handlers
 * @returns focusedIndex - Current card index with focus
 * @returns isRailFocused - Whether the rail container has keyboard focus
 * @returns railRef - React ref for the rail container element
 * @returns handleRailFocus - Handler for rail gaining focus
 * @returns handleRailBlur - Handler for rail losing focus
 * @returns handleRailKeyDown - Handler for keyboard events on the rail
 * @returns getCardProps - Function to get props for a card at given index (includes className and ref)
 * @returns registerWatchlistHandler - Register watchlist toggle handler for a card
 *
 * @example
 * ```tsx
 * const { railRef, handleRailKeyDown, getCardProps } = useRailNavigation({
 *   itemCount: titles.length,
 *   onAction: (index) => navigateToPlayer(titles[index]),
 *   onInfo: (index) => navigateToDetails(titles[index])
 * });
 *
 * <ul ref={railRef} onKeyDown={handleRailKeyDown} data-rail tabIndex={0}>
 *   {titles.map((title, i) => {
 *     const cardProps = getCardProps(i);
 *     return (
 *       <li key={title.id} ref={cardProps.ref} data-rail-item
 *           className={`group ${cardProps.className}`}>
 *         <Card title={title} />
 *       </li>
 *     );
 *   })}
 * </ul>
 * ```
 */
export function useRailNavigation({
  itemCount,
  onAction,
  onInfo,
  onWatchlist,
  preferInfoOnEnter = false,
  disableAutoScroll = false,
  initialIndex = 0,
}: UseRailNavigationOptions): UseRailNavigationReturn {
  const [focusedIndex, setFocusedIndex] = useState(initialIndex);
  const [isRailFocused, setIsRailFocused] = useState(false);
  const railRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<Map<number, HTMLElement>>(new Map());
  const watchlistHandlers = useRef<Map<number, () => void>>(new Map());
  const hasInitialized = useRef(false);

  // Clean up watchlist handlers when itemCount decreases to prevent memory leaks
  useEffect(() => {
    for (const [index] of watchlistHandlers.current) {
      if (index >= itemCount) {
        watchlistHandlers.current.delete(index);
      }
    }
  }, [itemCount]);

  // Clamp focusedIndex when itemCount changes to prevent out-of-bounds focus
  // Preserve initialIndex when items first load (itemCount goes from 0 to non-zero)
  useEffect(() => {
    if (itemCount === 0) {
      hasInitialized.current = false;
      setFocusedIndex(0);
    } else if (!hasInitialized.current) {
      // First time items load - use initialIndex if valid
      hasInitialized.current = true;
      if (initialIndex < itemCount) {
        setFocusedIndex(initialIndex);
      } else {
        setFocusedIndex(0);
      }
    } else if (focusedIndex >= itemCount) {
      // Clamp to valid range if items decrease
      setFocusedIndex(itemCount - 1);
    }
  }, [itemCount, focusedIndex, initialIndex]);

  // Custom smooth scroll with easing
  const smoothScrollTo = useCallback((element: HTMLElement) => {
    const targetPosition = element.getBoundingClientRect().top + window.scrollY;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition - window.innerHeight / 2 + element.offsetHeight / 2;
    const duration = 400; // ms
    let start: number | null = null;

    // Ease-in-out cubic function for smoother acceleration and deceleration
    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animation = (currentTime: number) => {
      start ??= currentTime;
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);

      window.scrollTo(0, startPosition + distance * easeProgress);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }, []);

  // Scroll focused card into view
  const scrollToCard = useCallback(
    (index: number) => {
      if (disableAutoScroll) return; // Skip auto-scroll for custom scroll behavior

      const card = cardRefs.current.get(index);
      if (card) {
        card.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    },
    [disableAutoScroll],
  );

  // Handle rail gaining focus
  const handleRailFocus = useCallback(() => {
    setIsRailFocused(true);
    if (!disableAutoScroll) {
      scrollToCard(focusedIndex);
    }
  }, [focusedIndex, scrollToCard, disableAutoScroll]);

  // Handle rail losing focus
  const handleRailBlur = useCallback(() => {
    setIsRailFocused(false);
  }, []);

  // Navigate to a specific card index
  const navigateToCard = useCallback(
    (newIndex: number): void => {
      const clampedIndex = Math.max(0, Math.min(itemCount - 1, newIndex));
      setFocusedIndex(clampedIndex);
      scrollToCard(clampedIndex);
    },
    [itemCount, scrollToCard],
  );

  // Helper: Navigate to previous rail (ArrowUp)
  const navigateToPreviousRail = useCallback(
    (e: React.KeyboardEvent) => {
      const currentRail = railRef.current;
      if (!currentRail) return;

      const allRails = document.querySelectorAll(RAIL_SELECTOR);
      const currentIndex = Array.from(allRails).indexOf(currentRail);

      if (currentIndex > 0) {
        e.preventDefault();
        const prevRail = allRails[currentIndex - 1] as HTMLElement;
        if (prevRail) {
          prevRail.focus({ preventScroll: true });
          setTimeout(() => {
            smoothScrollTo(prevRail);
          }, 0);
        }
      }
    },
    [smoothScrollTo],
  );

  // Helper: Navigate to next rail (ArrowDown)
  const navigateToNextRail = useCallback(
    (e: React.KeyboardEvent) => {
      const currentRail = railRef.current;
      if (!currentRail) return;

      const allRails = document.querySelectorAll(RAIL_SELECTOR);
      const currentIndex = Array.from(allRails).indexOf(currentRail);

      if (currentIndex < allRails.length - 1) {
        e.preventDefault();
        const nextRail = allRails[currentIndex + 1] as HTMLElement;
        if (nextRail) {
          nextRail.focus({ preventScroll: true });
          setTimeout(() => {
            smoothScrollTo(nextRail);
          }, 0);
        }
      }
    },
    [smoothScrollTo],
  );

  // Helper: Handle action shortcuts (Enter, Space, I, W)
  const handleActionKeys = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (preferInfoOnEnter && e.key === "Enter") {
          onInfo?.(focusedIndex);
        } else {
          onAction?.(focusedIndex);
        }
      } else if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        onInfo?.(focusedIndex);
      } else if (e.key === "w" || e.key === "W") {
        e.preventDefault();
        const handler = watchlistHandlers.current.get(focusedIndex);
        if (handler) {
          handler();
        } else {
          onWatchlist?.(focusedIndex);
        }
      }
    },
    [focusedIndex, preferInfoOnEnter, onAction, onInfo, onWatchlist],
  );

  // Handle keyboard navigation
  const handleRailKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Don't handle navigation if a dropdown menu is open
      const hasOpenMenu = document.querySelector('[role="menu"]') !== null;
      if (hasOpenMenu) {
        return;
      }

      // Horizontal arrow navigation within rail
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigateToCard(focusedIndex - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigateToCard(focusedIndex + 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        navigateToCard(0);
      } else if (e.key === "End") {
        e.preventDefault();
        navigateToCard(itemCount - 1);
      }
      // Vertical navigation between rails
      else if (e.key === "ArrowUp") {
        navigateToPreviousRail(e);
      } else if (e.key === "ArrowDown") {
        navigateToNextRail(e);
      }
      // Exit navigation mode
      else if (e.key === "Escape") {
        e.preventDefault();
        railRef.current?.blur();
      }
      // Action shortcuts
      else {
        handleActionKeys(e);
      }
    },
    [focusedIndex, itemCount, navigateToCard, navigateToPreviousRail, navigateToNextRail, handleActionKeys],
  );

  // Register a watchlist handler for a specific card
  const registerWatchlistHandler = useCallback((index: number, handler: () => void) => {
    watchlistHandlers.current.set(index, handler);
  }, []);

  // Get props for a card at given index
  const getCardProps = useCallback(
    (index: number) => {
      const isFocused = isRailFocused && focusedIndex === index;
      return {
        // Use class instead of data attribute for better type safety and performance
        className: isFocused ? FOCUSED_CLASS : "",
        isFocused, // Pass as separate prop for conditional logic
        ref: (el: HTMLElement | null) => {
          if (el) {
            cardRefs.current.set(index, el);
          } else {
            cardRefs.current.delete(index);
          }
        },
      };
    },
    [isRailFocused, focusedIndex],
  ); // Auto-focus first card when rail gains focus
  useEffect(() => {
    if (isRailFocused) {
      scrollToCard(focusedIndex);
    }
  }, [isRailFocused, focusedIndex, scrollToCard]);

  return {
    focusedIndex,
    isRailFocused,
    railRef,
    handleRailFocus,
    handleRailBlur,
    handleRailKeyDown,
    getCardProps,
    registerWatchlistHandler,
    navigateToIndex: navigateToCard,
  };
}
